/**
 * Como se numeran los documentos segun el pais.
 *
 * Hay dos modelos y no son intercambiables:
 *  - CAF (Chile): el SII autoriza un RANGO de folios y entrega un archivo que
 *    los ampara. El folio sale de ese rango y se agota.
 *  - Serie (Peru): el contribuyente DECLARA la serie y su sistema lleva el
 *    correlativo. No hay rango que autorice la autoridad ni archivo que cargar.
 *
 * De ahi que la pantalla de numeracion sea distinta en cada pais. Se mantiene
 * como lista explicita, y no como "todo lo que no es Chile", para que un pais
 * nuevo no herede una pantalla que su normativa no contempla.
 */
const PAISES_CON_SERIES = ['PE']

export function usaSeriesDeEmision(countryCode?: string | null): boolean {
  return PAISES_CON_SERIES.includes((countryCode || '').toUpperCase())
}

export function etiquetasNumeracion(countryCode?: string | null): {
  nav: string
  title: string
  subtitle: string
} {
  return usaSeriesDeEmision(countryCode)
    ? {
        nav: 'Series',
        title: 'Series de emisión',
        subtitle: 'Series y correlativos con los que se numeran los documentos.',
      }
    : {
        nav: 'Folios',
        title: 'Folios y CAF',
        subtitle: 'Folios autorizados y Códigos de Autorización del SII.',
      }
}

/**
 * Paises cuyo regimen contempla la COMUNICACION DE BAJA: pedirle a la autoridad
 * que deje sin efecto un comprobante que ya acepto.
 *
 * No es lo mismo que anular. En Chile un DTE aceptado NO se da de baja: se
 * reversa con una nota de credito, asi que ofrecer la accion alli seria ofrecer
 * un tramite que no existe.
 *
 * Lista explicita por la misma razon que `PAISES_CON_SERIES`: un pais nuevo no
 * debe heredar un tramite que su normativa no contempla.
 */
const PAISES_CON_COMUNICACION_DE_BAJA = ['PE']

export function paisUsaComunicacionDeBaja(countryCode?: string | null): boolean {
  return PAISES_CON_COMUNICACION_DE_BAJA.includes((countryCode || '').toUpperCase())
}

/**
 * Códigos del catálogo 54 de SUNAT — bienes y servicios sujetos a detracción.
 *
 * Solo los que un tenant de servicios necesita elegir. La lista completa vive en
 * el backend (`pe_sunat/catalogs.py`), que es la fuente: acá está lo que la UI
 * ofrece, para no obligar a memorizar códigos.
 */
export const CODIGOS_DETRACCION: { codigo: string; nombre: string; tasa: string }[] = [
  { codigo: '022', nombre: 'Otros servicios empresariales', tasa: '12' },
  { codigo: '037', nombre: 'Demás servicios gravados con el IGV', tasa: '12' },
  { codigo: '012', nombre: 'Intermediación laboral y tercerización', tasa: '12' },
  { codigo: '020', nombre: 'Mantenimiento y reparación de bienes muebles', tasa: '12' },
  { codigo: '019', nombre: 'Arrendamiento de bienes', tasa: '10' },
  { codigo: '021', nombre: 'Movimiento de carga', tasa: '10' },
  { codigo: '024', nombre: 'Comisión mercantil', tasa: '10' },
  { codigo: '025', nombre: 'Fabricación de bienes por encargo', tasa: '10' },
  { codigo: '026', nombre: 'Servicio de transporte de personas', tasa: '10' },
  { codigo: '030', nombre: 'Contratos de construcción', tasa: '4' },
  { codigo: '040', nombre: 'Bien inmueble gravado con IGV', tasa: '4' },
]

/**
 * Si el país usa el sistema de detracciones (SPOT).
 *
 * Hoy es exclusivo de Perú. La lista es explícita a propósito, igual que en
 * `usaSeriesDeEmision`: un país nuevo NO debe heredar un régimen tributario que
 * su normativa no contempla.
 */
export function paisUsaDetraccion(countryCode?: string | null): boolean {
  return (countryCode || '').toUpperCase() === 'PE'
}

/**
 * Los tipos de operación del catálogo 51 de SUNAT que se pueden declarar al
 * emitir. Espejo de `OPERATION_TYPES_SELECTABLE` del backend.
 *
 * ⚠️ Hay NUEVE códigos de exportación y elegir el equivocado declara ante SUNAT
 * una operación distinta de la real: el `0200` es de **bienes** y una consultora
 * que exporta servicios va en `0201`. Por eso se listan con su descripción
 * completa en vez de un genérico «exportación».
 *
 * ⚠️ El de la detracción (`1001`) NO está y no debe estar: lo pone el generador
 * cuando el documento resulta afecto. Ofrecerlo dejaría declarar una detracción
 * que nadie calculó.
 */
export const CODIGOS_TIPO_OPERACION_PE = [
  { codigo: '', descripcion: 'Venta interna' },
  { codigo: '0200', descripcion: 'Exportación de bienes' },
  { codigo: '0201', descripcion: 'Exportación de servicios — realizados íntegramente en el país' },
  { codigo: '0202', descripcion: 'Exportación de servicios — hospedaje a no domiciliados' },
  { codigo: '0203', descripcion: 'Exportación de servicios — transporte de navieras' },
  { codigo: '0204', descripcion: 'Exportación de servicios — naves y aeronaves de bandera extranjera' },
  { codigo: '0205', descripcion: 'Exportación de servicios — paquete turístico' },
  { codigo: '0206', descripcion: 'Exportación de servicios — complementarios al transporte de carga' },
  { codigo: '0207', descripcion: 'Exportación de servicios — energía eléctrica a sujetos en ZED' },
  { codigo: '0208', descripcion: 'Exportación de servicios — realizados parcialmente en el extranjero' },
] as const
